import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { getThemeClass } from '../../lib/theme'
import { useAuth } from '../../lib/auth.context'
import { useToast } from '../../hooks/use-toast'
import { supabase } from '../../lib/supabase'
import { User } from '@supabase/supabase-js'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog'
import { TOTPEnrollment } from '../../components/auth/totp-enrollment'

interface AMREntry {
  method: string
  timestamp: number
}

interface JWTPayload {
  sub: string
  aud: string
  exp: number
  iat: number
  email: string
  phone: string
  app_metadata: {
    partner_id: string
    provider: string
    providers: string[]
    user_role: string
  }
  user_metadata: {
    first_name: string
    last_name: string
  }
  role: string
  aal: string
  amr: AMREntry[]
  session_id: string
  is_anonymous: boolean
}

export function UserInfoPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isReenrollingTOTP, setIsReenrollingTOTP] = useState(false)
  const [userInfo, setUserInfo] = useState<User | null>(null)
  const [showTOTPDialog, setShowTOTPDialog] = useState(false)
  const { toast } = useToast()
  const { getToken, refreshSession } = useAuth()
  const navigate = useNavigate()
  const token = getToken()
  const decodedToken = token ? JSON.parse(atob(token.split('.')[1])) as JWTPayload : null

  const fetchUserInfo = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      if (error) throw error
      setUserInfo(user)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch user information',
        variant: 'destructive',
      })
    }
  }

  useEffect(() => {
    fetchUserInfo()
  }, [])

  const handleRefreshToken = async () => {
    setIsLoading(true)
    try {
      await refreshSession()
      await fetchUserInfo()
      toast({
        title: 'Success',
        description: 'Token refreshed successfully',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to refresh token',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisableTOTP = async () => {
    setIsReenrollingTOTP(true)
    try {
      console.log('Disabling TOTP')

      // Find TOTP factor from user info
      const totpFactor = userInfo?.factors?.find(factor => factor.factor_type === 'totp')
      if (!totpFactor) {
        throw new Error('No TOTP factor found')
      }

      console.log('TOTP factor found:', totpFactor)
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: totpFactor.id })
      if (unenrollError) throw unenrollError

      console.log('TOTP factor unenrolled')
      // Refresh token and user info after disabling TOTP
      await refreshSession()
      await fetchUserInfo()

      toast({
        title: 'Success',
        description: 'TOTP has been disabled successfully',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to disable TOTP',
        variant: 'destructive',
      })
    } finally {
      setIsReenrollingTOTP(false)
    }
  }

  const handleTOTPEnrollmentComplete = async () => {
    setShowTOTPDialog(false)
    await refreshSession()
    await fetchUserInfo()
  }

  if (!decodedToken) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6">
          <h1 className="text-xl font-bold mb-4">No active session</h1>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </Card>
      </div>
    )
  }

  const fullName = decodedToken.user_metadata.first_name && decodedToken.user_metadata.last_name
    ? `${decodedToken.user_metadata.first_name} ${decodedToken.user_metadata.last_name}`
    : 'N/A'

  const expiryDate = new Date(decodedToken.exp * 1000).toISOString()
  const hasTOTPFactor = userInfo?.factors?.some(factor => factor.factor_type === 'totp') ?? false

  return (
    <>
      <div className="flex min-h-screen">
        <div className={`flex-1 flex flex-col ${getThemeClass('gradients.primary')}`}>
          <div className="flex-1 p-6 overflow-hidden flex flex-col">
            <Card className={`w-full max-w-4xl mx-auto flex-1 flex flex-col ${getThemeClass('components.card.base')}`}>
              <div className="p-6 flex flex-col flex-1">
                <h1 className={`mb-6 text-2xl font-bold ${getThemeClass('components.text.heading')}`}>User Information</h1>

                <div className="mb-6 space-y-2">
                  <p><strong>Email:</strong> {decodedToken.email || 'N/A'}</p>
                  <p><strong>Name:</strong> {fullName}</p>
                  <p><strong>UserId:</strong> {decodedToken.sub}</p>
                  <p><strong>AAL:</strong> {decodedToken.aal}</p>
                  <p><strong>User Role:</strong> {decodedToken.app_metadata.user_role || 'N/A'}</p>
                  <p><strong>Session Expires:</strong> {expiryDate}</p>
                </div>

                <div className="flex gap-4 mb-6">
                  {!hasTOTPFactor ? (
                    <Button
                      onClick={() => setShowTOTPDialog(true)}
                      className={getThemeClass('components.button.primary')}
                    >
                      Enroll TOTP
                    </Button>
                  ) : (
                    <Button
                      onClick={handleDisableTOTP}
                      className={getThemeClass('components.button.destructive')}
                      disabled={isReenrollingTOTP}
                    >
                      {isReenrollingTOTP ? 'Disabling TOTP...' : 'Disable TOTP'}
                    </Button>
                  )}
                </div>

                <div className="flex gap-4 mb-6 pb-4 border-b">
                  <Button
                    onClick={handleRefreshToken}
                    className={getThemeClass('components.button.primary')}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Refreshing Token...' : 'Refresh Token'}
                  </Button>
                </div>

                <Tabs defaultValue="jwt" className="flex-1 flex flex-col">
                  <TabsList>
                    <TabsTrigger value="jwt">JWT Token</TabsTrigger>
                    <TabsTrigger value="user-info">User Info</TabsTrigger>
                  </TabsList>

                  <TabsContent value="jwt" className="flex-1 mt-0">
                    <div className="overflow-auto h-[600px] bg-gray-100 rounded-md">
                      <pre className="p-4">
                        {JSON.stringify(decodedToken, null, 2)}
                      </pre>
                    </div>
                  </TabsContent>

                  <TabsContent value="user-info" className="flex-1 mt-0">
                    <div className="overflow-auto h-[600px] bg-gray-100 rounded-md">
                      <pre className="p-4">
                        {JSON.stringify(userInfo, null, 2)}
                      </pre>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showTOTPDialog} onOpenChange={setShowTOTPDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enhance your account security by setting up two-factor authentication using an authenticator app.
            </DialogDescription>
          </DialogHeader>
          <TOTPEnrollment onComplete={handleTOTPEnrollmentComplete} isEmbedded />
        </DialogContent>
      </Dialog>
    </>
  )
}
